import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaTimes } from 'react-icons/fa';
import api from '../../services/api';
import List from '../../components/Crud/List';
import { useAuth } from '../../contexts/auth';
import { toIdentifierMask } from '../../services/mask';
import CustomersFilter, { CustomerFilterFields } from './CustomersFilter';
import {
  getLastFilterByKey,
  upsertFilterToKey,
} from '../../services/filterService';
import CustomButton from '../../components/CustomButton/CustomButton';

type CustomerModel = {
  id: number;
  type: 1 | 2;
  identifier: string;
  name: string;
  alias?: string;
};

export default function CustomersIndex() {
  const pageKey = 'customers';
  const [isLoading, setIsLoading] = useState(true);
  const [customers, setCustomers] = useState([] as CustomerModel[]);
  const [filterFields, setFilterFields] = useState<CustomerFilterFields>(
    null as unknown as CustomerFilterFields
  );
  const {
    user: { organization },
  } = useAuth();

  async function getCustomers(data: CustomerFilterFields) {
    if (organization) {
      setIsLoading(true);
      const response = await api.get(
        `organizations/${organization.id}/customers`,
        { params: data }
      );
      setCustomers(response.data.data);
      setIsLoading(false);
    }
  }

  const onFilterSubmit = (data: CustomerFilterFields) => {
    const filter = new CustomerFilterFields(data);
    upsertFilterToKey(pageKey, filter);
    setFilterFields(filter);
    getCustomers(data);
  };

  const remove = useCallback(
    async (customer: CustomerModel) => {
      try {
        const displayName = `${customer.name}`;
        if (
          // eslint-disable-next-line no-restricted-globals
          confirm(
            `Você tem certeza que deseja remover o cliente "${displayName}"? Essa ação não poderá ser desfeita.`
          )
        ) {
          await api.delete(
            `organizations/${organization.id}/customers/${customer.id}`
          );

          const filters = getLastFilterByKey(pageKey, CustomerFilterFields);
          getCustomers(filters);
          toast.success(`O cliente "${displayName}" foi removido`);
        }
      } catch (e) {
        toast.error(
          'Houve um problema ao tentar remover o cliente. Tente novamente.'
        );
      }
    },
    [organization.id, getCustomers, getLastFilterByKey, toast]
  );

  useEffect(() => {
    if (organization) {
      const filters = getLastFilterByKey(pageKey, CustomerFilterFields);
      setFilterFields(filters);
      getCustomers(filters);
    }
  }, [organization]);

  return (
    <List
      isLoading={isLoading}
      title='Clientes'
      newItemUrl='new'
      filter={
        <CustomersFilter
          filterFields={filterFields}
          onSubmit={onFilterSubmit}
        />
      }
    >
      <table className='table table-hover'>
        <thead>
          <tr>
            <th scope='col'>#</th>
            <th scope='col'>CPF/CNPJ</th>
            <th scope='col'>Nome</th>
            <th scope='col'>&nbsp;</th>
          </tr>
        </thead>
        <tbody>
          {customers.map((i) => {
            return (
              <tr key={i.id}>
                <th scope='row'>{i.id}</th>
                <td>
                  <Link to={`${i.id}/edit`}>
                    {toIdentifierMask(i.identifier, i.type)}
                  </Link>
                </td>
                <td>
                  {i.name}
                  {i.type === 1 ? (
                    <div className='text-muted small'>{i.alias}</div>
                  ) : (
                    ''
                  )}
                </td>
                <td className='text-end'>
                  <CustomButton
                    className='btn btn-sm btn-danger'
                    type='button'
                    handleClick={() => remove(i)}
                  >
                    <FaTimes />
                  </CustomButton>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </List>
  );
}
